import { FC } from "react";
import {
  CircleCheckIcon,
  LucideIcon,
  SearchIcon,
  ShieldPlusIcon,
  UserIcon,
} from "lucide-react-native";

import { VnBox } from "@/components/VnBox";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { OnboardingScreenType } from "@/domain/onboarding";
import {
  OnboardingNextStepsSectionType,
  useOnboardingNextStepsContent,
} from "@/hooks/useOnboardingNextStepsContent";
import { cn } from "@/utils/tailwind";

import nextStepsBackground from "./assets/next-steps-background.jpg";
import { PhotoCardHeader } from "./PhotoCardHeader";
import { PhotoCardStepFrame } from "./PhotoCardStepFrame";

const IMAGE_POSITION = { left: "50%", top: "0%" } as const;

const SECTION_ICONS: LucideIcon[] = [UserIcon, SearchIcon, CircleCheckIcon];

const Section: FC<OnboardingNextStepsSectionType> = ({
  number,
  title,
  description,
  duration,
  isDurationHidden,
}) => {
  const isFirstSection = number === 1;
  const icon = SECTION_ICONS[number - 1] ?? CircleCheckIcon;

  return (
    <VnBox
      className={cn("flex-row items-center gap-4 rounded-md p-4", {
        "bg-secondary": isFirstSection,
        "bg-black/15": !isFirstSection,
      })}>
      <VnIcon
        as={icon}
        size={28}
        className={cn({
          "text-sidebar": isFirstSection,
          "text-primary": !isFirstSection,
        })}
      />
      <VnBox className="min-w-0 flex-1">
        <VnText
          variant="lgBold"
          className={cn({
            "text-black/80": isFirstSection,
            "text-white": !isFirstSection,
          })}
          {...title}
        />
        <VnText
          className={cn({
            "text-black/70": isFirstSection,
            "text-white": !isFirstSection,
          })}
          {...description}
        />
      </VnBox>
      {!isDurationHidden && (
        <VnBox
          className={cn("shrink-0 rounded-md border px-2 py-0.5", {
            "bg-sidebar border-sidebar": isFirstSection,
            "border-primary": !isFirstSection,
          })}>
          <VnText
            variant="mdBold"
            className={cn({
              "text-white": isFirstSection,
              "text-primary": !isFirstSection,
            })}
            {...duration}
          />
        </VnBox>
      )}
    </VnBox>
  );
};

export const PhotoCardNextStepsStep: FC<OnboardingScreenType> = layout => {
  const { title, description, sections } = useOnboardingNextStepsContent();

  return (
    <PhotoCardStepFrame
      {...layout}
      image={nextStepsBackground}
      imagePosition={IMAGE_POSITION}>
      <PhotoCardHeader
        icon={<VnIcon as={ShieldPlusIcon} size={48} className="text-primary" />}
        title={title}
        description={description}
      />

      <VnBox className="gap-3">
        {sections.map(section => (
          <Section key={section.number} {...section} />
        ))}
      </VnBox>
    </PhotoCardStepFrame>
  );
};
